import React, { useState } from 'react';
import axios from 'axios';
import { useLanguage } from '../context/LanguageContext';
import { useAuth } from '../context/AuthContext';
import { toast } from 'sonner';
import { formatApiError } from '../utils/errorHandler';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { User, Mail, Lock, Save } from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const ProfilePage = () => {
  const { t, language } = useLanguage();
  const { user, refreshUser } = useAuth();
  const [saving, setSaving] = useState(false);
  const [formData, setFormData] = useState({
    name: user?.name || '',
    email: user?.email || '',
    current_password: '',
    new_password: '',
    confirm_password: ''
  });

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (formData.new_password && formData.new_password !== formData.confirm_password) {
      toast.error(language === 'tr' ? 'Yeni şifreler eşleşmiyor' : 'New passwords do not match');
      return;
    }
    
    setSaving(true);
    try {
      const payload = {};
      if (formData.name !== user?.name) payload.name = formData.name;
      if (formData.email !== user?.email) payload.email = formData.email;
      if (formData.new_password) {
        payload.current_password = formData.current_password;
        payload.new_password = formData.new_password;
      }
      
      if (Object.keys(payload).length === 0) {
        toast.info(language === 'tr' ? 'Değişiklik yapılmadı' : 'No changes');
        setSaving(false);
        return;
      }
      
      await axios.put(`${API_URL}/api/auth/profile`, payload, { withCredentials: true });
      toast.success(t('profileUpdated'));
      setFormData(prev => ({ ...prev, current_password: '', new_password: '', confirm_password: '' }));
      if (refreshUser) refreshUser();
    } catch (error) {
      toast.error(formatApiError(error));
    } finally {
      setSaving(false);
    }
  };

  const roleLabels = {
    super_admin: 'Süper Yönetici',
    company_admin: 'Şirket Yöneticisi',
    finance: 'Finans',
    operations: 'Operasyon',
    maintenance: 'Bakım',
    staff: 'Personel',
    tenant: 'Kiracı'
  };

  return (
    <div className="max-w-xl mx-auto space-y-6" data-testid="profile-page">
      <h1 className="font-heading text-3xl font-bold tracking-tight">{t('profile')}</h1>
      
      {/* User Info Card */}
      <div className="bg-white border border-gray-200 p-6">
        <div className="flex items-center gap-4 mb-6">
          <div className="w-16 h-16 bg-black text-white flex items-center justify-center font-bold text-2xl">
            {user?.name?.charAt(0)?.toUpperCase()}
          </div>
          <div>
            <p className="font-heading text-xl font-bold">{user?.name}</p>
            <p className="text-sm text-gray-500">{roleLabels[user?.role] || user?.role}</p>
          </div>
        </div>

        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <label className="text-sm font-medium flex items-center gap-2">
              <User className="w-4 h-4" />
              {t('name')}
            </label>
            <Input
              value={formData.name}
              onChange={(e) => setFormData(prev => ({ ...prev, name: e.target.value }))}
              className="rounded-none mt-1"
              data-testid="profile-name-input"
            />
          </div>

          <div>
            <label className="text-sm font-medium flex items-center gap-2">
              <Mail className="w-4 h-4" />
              {t('email')}
            </label>
            <Input
              type="email"
              value={formData.email}
              onChange={(e) => setFormData(prev => ({ ...prev, email: e.target.value }))}
              className="rounded-none mt-1"
              data-testid="profile-email-input"
            />
          </div>

          <div className="border-t border-gray-200 pt-4 mt-4">
            <p className="text-sm font-medium mb-3 flex items-center gap-2">
              <Lock className="w-4 h-4" />
              {language === 'tr' ? 'Şifre Değiştir' : 'Change Password'}
            </p>
            <div className="space-y-3">
              <div>
                <label className="text-xs text-gray-500">{t('currentPassword')}</label>
                <Input
                  type="password"
                  value={formData.current_password}
                  onChange={(e) => setFormData(prev => ({ ...prev, current_password: e.target.value }))}
                  className="rounded-none mt-1"
                  data-testid="current-password-input"
                />
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="text-xs text-gray-500">{t('newPassword')}</label>
                  <Input
                    type="password"
                    value={formData.new_password}
                    onChange={(e) => setFormData(prev => ({ ...prev, new_password: e.target.value }))}
                    className="rounded-none mt-1"
                    data-testid="new-password-input"
                  />
                </div>
                <div>
                  <label className="text-xs text-gray-500">{language === 'tr' ? 'Şifre Tekrar' : 'Confirm Password'}</label>
                  <Input
                    type="password"
                    value={formData.confirm_password}
                    onChange={(e) => setFormData(prev => ({ ...prev, confirm_password: e.target.value }))}
                    className="rounded-none mt-1"
                    data-testid="confirm-password-input"
                  />
                </div>
              </div>
            </div>
          </div>

          <Button 
            type="submit" 
            disabled={saving}
            className="w-full bg-black text-white rounded-none hover:bg-gray-800 mt-4"
            data-testid="profile-save-btn"
          >
            <Save className="w-4 h-4 mr-2" />
            {saving ? t('loading') : t('save')}
          </Button>
        </form>
      </div>
    </div>
  );
};

export default ProfilePage;
